import { useEffect, useRef, useState } from 'react';
import { THUMB_STATUS } from '@src/constants/timeline';
import { queueDecodeAudioPeaks } from '@src/services/audioWaveform';
import createFifoCache from '@src/services/fifoCache';
import {
  normalizeCacheKey,
  readCache,
  writeCache,
} from '@src/services/mediaExtractor';

const idle = (status) => ({ status, peaks: [], duration: 0 });

// Keep finished peaks so remounting a track (e.g. collapse/expand the timeline)
// doesn't decode again. Shared by every track and big enough to hold a whole
// music lane plus the video; peaks are small arrays, so it's cheap.
const resultCache = createFifoCache(32);

/**
 * Decode a url into waveform peaks, once per url and cached. `opts` sets the
 * peak density per track (pass a stable, module-level object). Decodes go
 * through a shared queue (queueDecodeAudioPeaks) so too many clips don't decode
 * at once. Returns `{ status, peaks, duration }` (duration in seconds, 0 until
 * decoded); FAILED means the caller shows no waveform (audio couldn't be
 * decoded, or it's too big).
 *
 * `enabled` lets a caller hold the decode back until it is worth starting.
 * Defaults to true, so callers that have nothing to wait for stay as they are.
 */
const useAudioWaveform = (url, opts, enabled = true) => {
  const [state, setState] = useState(
    () => resultCache.get(normalizeCacheKey(url)) || idle(THUMB_STATUS.LOADING),
  );
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;

    // Not our turn yet: the media track holds the waveform back until the
    // filmstrip is done, so a 169MB download (measured ~117s) stops competing
    // with the keyframe ranges and the preview <video> for bandwidth.
    if (!enabled) return undefined;

    const cacheKey = normalizeCacheKey(url);
    const cached = resultCache.get(cacheKey);
    if (cached) {
      setState(cached);
      return undefined;
    }
    setState(idle(THUMB_STATUS.LOADING));

    // No AbortController: the decode is shared between clips, so one unmounting
    // must not cancel it for the others — just ignore the result if we're gone.
    // A finished decode still fills the cache for next time.
    // Peaks are a plain number array (maxPeaks entries at most), so IndexedDB
    // stores them as-is. Caching them is what removes the whole-file download
    // from every visit after the first.
    const load = async () => {
      const stored = await readCache(url, 'peaks');
      if (stored) return stored;
      const decoded = await queueDecodeAudioPeaks(url, opts);
      if (decoded) writeCache(url, 'peaks', decoded);
      return decoded;
    };

    load().then((result) => {
      if (cancelledRef.current) return;
      if (!result) {
        setState(idle(THUMB_STATUS.FAILED));
        return;
      }
      const ready = {
        status: THUMB_STATUS.READY,
        peaks: result.peaks,
        duration: result.duration,
      };
      resultCache.set(cacheKey, ready);
      setState(ready);
    });

    return () => {
      cancelledRef.current = true;
    };
  }, [url, opts, enabled]);

  return state;
};

export default useAudioWaveform;
